import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Activity,
} from 'lucide-react';
import type {
  ComplianceStats,
  FrameworkStats,
  ComplianceIncident,
} from '../types/compliance';
import './Dashboard.css';

// Default empty state values
const defaultStats: ComplianceStats = {
  totalControls: 0,
  passingControls: 0,
  failingControls: 0,
  needsReviewControls: 0,
  complianceScore: 0,
  evidencePending: 0,
  evidenceExpiringSoon: 0,
  openIncidents: 0,
  criticalIncidents: 0,
};

interface AuditLogEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  previous_status?: string;
  new_status?: string;
  actor: string;
  details?: { name?: string; title?: string };
  created_at: string;
}


function formatAuditLogEntry(entry: AuditLogEntry): { id: string; action: string; target: string; time: string; type: string } {
  const getActionLabel = (action: string, entityType: string, newStatus?: string) => {
    if (action === 'status_changed' && newStatus) {
      if (newStatus === 'passing' || newStatus === 'approved' || newStatus === 'resolved') return `${entityType} passed`;
      if (newStatus === 'failing' || newStatus === 'rejected') return `${entityType} failed`;
      return `${entityType} ${newStatus.replace('_', ' ')}`;
    }
    return `${entityType} ${action}`;
  };

  const getType = (action: string, newStatus?: string) => {
    if (action === 'created') return 'info';
    if (newStatus === 'passing' || newStatus === 'approved' || newStatus === 'resolved') return 'success';
    if (newStatus === 'failing' || newStatus === 'rejected') return 'error';
    if (newStatus === 'needs_review' || action === 'status_changed') return 'warning';
    return 'info';
  };

  const target = entry.details?.name || entry.details?.title || entry.entity_id;
  const timeDiff = Date.now() - new Date(entry.created_at).getTime();
  const minutes = Math.floor(timeDiff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const time = days > 0 ? `${days}d ago` : hours > 0 ? `${hours}h ago` : minutes > 0 ? `${minutes}m ago` : 'Just now';

  return {
    id: entry.id,
    action: getActionLabel(entry.action, entry.entity_type.charAt(0).toUpperCase() + entry.entity_type.slice(1), entry.new_status),
    target,
    time,
    type: getType(entry.action, entry.new_status),
  };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ComplianceStats>(defaultStats);
  const [frameworkStats, setFrameworkStats] = useState<FrameworkStats[]>([]);
  const [recentIncidents, setRecentIncidents] = useState<ComplianceIncident[]>([]);
  const [activityLog, setActivityLog] = useState<{ id: string; action: string; target: string; time: string; type: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [statsRes, frameworkRes, incidentsRes, auditRes] = await Promise.all([
          fetch('/api/compliance/stats'),
          fetch('/api/compliance/frameworks/stats'),
          fetch('/api/compliance/incidents?limit=5'),
          fetch('/api/compliance/audit-log?limit=10'),
        ]);

        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data);
        }

        if (frameworkRes.ok) {
          const data = await frameworkRes.json();
          setFrameworkStats(data);
        }

        if (incidentsRes.ok) {
          const data = await incidentsRes.json();
          setRecentIncidents(data);
        }

        if (auditRes.ok) {
          const data: AuditLogEntry[] = await auditRes.json();
          if (data && data.length > 0) {
            setActivityLog(data.map(formatAuditLogEntry));
          }
        }
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'green';
    if (score >= 70) return 'yellow';
    return 'red';
  };

  const getSeverityClass = (severity: string) => {
    switch (severity) {
      case 'critical': return 'badge-critical';
      case 'error': return 'badge-failing';
      case 'warning': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Compliance Dashboard</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#a6adc8' }}>
          Loading dashboard data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Compliance Dashboard</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f38ba8' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Compliance Dashboard</h1>
        <p>Monitor your organization's compliance posture across all frameworks</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-4 stats-grid">
        <div className="stat-card clickable" onClick={() => navigate('/controls')}>
          <div className="stat-icon green">
            <Shield size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.complianceScore}%</span>
            <span className="stat-label">Overall Score</span>
          </div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/controls')}>
          <div className="stat-icon blue">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.passingControls}/{stats.totalControls}</span>
            <span className="stat-label">Controls Passing</span>
          </div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/incidents')}>
          <div className="stat-icon red">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.openIncidents}</span>
            <span className="stat-label">Open Incidents</span>
          </div>
          {stats.criticalIncidents > 0 && (
            <span className="badge badge-critical">{stats.criticalIncidents} Critical</span>
          )}
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/evidence')}>
          <div className="stat-icon yellow">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.evidencePending}</span>
            <span className="stat-label">Evidence Pending</span>
          </div>
          {stats.evidenceExpiringSoon > 0 && (
            <span className="badge badge-warning">{stats.evidenceExpiringSoon} Expiring</span>
          )}
        </div>
      </div>

      {/* Framework Overview and Incidents */}
      <div className="grid grid-2 dashboard-main">
        {/* Framework Cards */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Framework Compliance</h2>
          </div>
          <div className="framework-list">
            {frameworkStats.map((fw) => (
              <div key={fw.framework} className="framework-item">
                <div className="framework-header">
                  <span className="framework-name">{fw.framework}</span>
                  <span className={`framework-score ${getScoreColor(fw.compliancePercentage)}`}>
                    {fw.compliancePercentage}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className={`progress-bar-fill ${getScoreColor(fw.compliancePercentage)}`}
                    style={{ width: `${fw.compliancePercentage}%` }}
                  />
                </div>
                <div className="framework-details">
                  <span className="passing">
                    <CheckCircle size={14} /> {fw.passing} passing
                  </span>
                  {fw.failing > 0 && (
                    <span className="failing">
                      <XCircle size={14} /> {fw.failing} failing
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Incidents */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Recent Incidents</h2>
            <a href="/incidents" className="btn btn-secondary">View All</a>
          </div>
          <div className="incidents-list">
            {recentIncidents.map((incident) => (
              <div key={incident.id} className="incident-item">
                <div className="incident-header">
                  <span className={`badge ${getSeverityClass(incident.severity)}`}>
                    {incident.severity}
                  </span>
                  <span className="incident-time">{formatTimeAgo(incident.createdAt)}</span>
                </div>
                <h3 className="incident-title">{incident.title}</h3>
                <p className="incident-description">{incident.description}</p>
                <div className="incident-meta">
                  {incident.framework && (
                    <span className="incident-framework">{incident.framework}</span>
                  )}
                  <span className="incident-status">{incident.status.replace('_', ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity Log */}
      <div className="card activity-card">
        <div className="card-header">
          <h2 className="card-title">
            <Activity size={20} />
            Recent Activity
          </h2>
        </div>
        <div className="activity-list">
          {activityLog.map((activity) => (
            <div key={activity.id} className="activity-item">
              <div className={`activity-dot ${activity.type}`} />
              <div className="activity-content">
                <span className="activity-action">{activity.action}</span>
                <span className="activity-target">{activity.target}</span>
              </div>
              <span className="activity-time">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
